// Shared client-side search/filter helpers that match by token-prefix
// instead of raw substring.
//
// Background: a substring filter ("te" matches "Wastewater") surfaces
// matches users do not visually associate with what they typed. We
// want the same rule that PR #458 established for the field-staff
// "Add asset" dialog applied across every client-side search bar:
//
//   - A row is a PRIMARY match when the query is a case-insensitive
//     prefix of at least one whitespace- or hyphen-separated token in
//     ANY of the row's searchable fields.
//       e.g. query "te" matches "Telephone", "Air Curtain - Telemetry"
//   - A row is a SECONDARY match when the query appears as a substring
//     in some searchable field but is NOT a token prefix.
//       e.g. query "te" matches "Wastewater Pump" (substring only)
//   - A row is dropped entirely when it has neither.
//
// Most flat client-side lists in this app use strict primary-only
// filtering — that's the explicit user request ("I should not be able
// to search for 'te' and see something with a 'te' in it unless the
// 'te' is at the start"). The classifier is exposed for callers that
// need the two-bucket "primary + Did you mean…?" UI rendered by the
// Add-asset dialog.
//
// Whitespace AND hyphen count as token boundaries. Asset names and
// site/building names regularly contain hyphens ("Air Curtain -
// Telemetry", "Wastewater-Pump", "BLDG-001") and users naturally
// treat them as word breaks when typing.

// Hyphen is at the end of the character class so it does not need
// escaping (eslint:no-useless-escape).
const TOKEN_BOUNDARY = /[\s-]+/

/**
 * Returns true if `query` matches `value` under the token-prefix rule.
 *
 * Match rules (in order):
 *   1. Whole-string case-insensitive prefix on the raw value
 *      ("BLDG-002" matches "BLDG-002 Annex"). This is the natural
 *      "you typed the start of the thing you see" case and avoids
 *      surprising the user when the query itself contains a token
 *      boundary like hyphen or space.
 *   2. Every whitespace- or hyphen-separated token in the query is a
 *      prefix of at least one token in the value. The matching is
 *      AND across query tokens, OR across value tokens — so
 *      "te" matches "Air Telephone", "fire ex" matches "Fire
 *      Extinguisher", "001 floor" matches "BLDG-001 Floor 2".
 *
 * Mid-token substrings are intentionally excluded: "te" does not
 * match "Wastewater Pump" (no token starts with "te"). This is the
 * exact behavior requested in the PR-#458 feedback.
 *
 * Empty / whitespace-only queries always return true so callers can
 * treat them as a no-op.
 */
export function hasTokenPrefix(value: string, query: string): boolean {
  const normalisedQuery = query.trim().toLowerCase()
  if (normalisedQuery.length === 0) return true
  const loweredValue = value.toLowerCase()

  // Fast path: whole-string prefix on the raw value covers the case
  // where the query itself contains token boundaries the user is
  // intentionally typing (e.g. "BLDG-002" matching "BLDG-002").
  if (loweredValue.startsWith(normalisedQuery)) return true

  // Token-AND: every query token must be a token-prefix of some
  // value token. Single-word queries reduce to "any value token
  // starts with the query".
  const valueTokens = loweredValue.split(TOKEN_BOUNDARY).filter(nonEmpty)
  const queryTokens = normalisedQuery.split(TOKEN_BOUNDARY).filter(nonEmpty)
  if (queryTokens.length === 0) return true
  for (const qToken of queryTokens) {
    if (!someStartsWith(valueTokens, qToken)) return false
  }
  return true
}

function nonEmpty(s: string): boolean {
  return s.length > 0
}

function someStartsWith(tokens: readonly string[], prefix: string): boolean {
  for (const token of tokens) {
    if (token.startsWith(prefix)) return true
  }
  return false
}

/**
 * Multi-field convenience: returns true if `query` token-prefixes ANY
 * of the supplied values. Null / undefined values are skipped (handy
 * for nullable PowerSync columns and optional fields like FLOC).
 */
export function hasTokenPrefixOnAny(
  values: ReadonlyArray<string | null | undefined>,
  query: string,
): boolean {
  for (const value of values) {
    if (value !== null && value !== undefined && hasTokenPrefix(value, query)) {
      return true
    }
  }
  return false
}

export interface ClassifiedRows<T> {
  primary: T[]
  secondary: T[]
}

/**
 * Splits `rows` into a primary / secondary bucket based on whether
 * the query is a token-prefix of any value returned by `getFields`.
 *
 * - primary  : query is a token-prefix of at least one field
 * - secondary: query appears as a substring of at least one field but
 *              does NOT token-prefix any field
 * - dropped  : query does not appear in any field at all
 *
 * Empty / whitespace-only queries are treated as a pass-through —
 * every row lands in `primary`, `secondary` stays empty. This keeps
 * idle-but-warm states (e.g. a stale list after a search is cleared)
 * visually intact.
 *
 * `getFields` may return null/undefined for missing optional values;
 * those are skipped, not coerced to empty strings.
 */
export function classifyByTokenPrefix<T>(
  rows: readonly T[],
  query: string,
  getFields: (row: T) => ReadonlyArray<string | null | undefined>,
): ClassifiedRows<T> {
  const normalisedQuery = query.trim().toLowerCase()

  if (normalisedQuery.length === 0) {
    return { primary: [...rows], secondary: [] }
  }

  const primary: T[] = []
  const secondary: T[] = []

  for (const row of rows) {
    const fields = getFields(row)
    let isPrimary = false
    let isSecondary = false
    for (const field of fields) {
      if (field === null || field === undefined) continue
      const lowered = field.toLowerCase()
      if (hasTokenPrefixLowered(lowered, normalisedQuery)) {
        isPrimary = true
        break
      }
      if (lowered.includes(normalisedQuery)) {
        isSecondary = true
        // Don't break — a later field might be a token-prefix match,
        // which would promote the row to primary.
      }
    }
    if (isPrimary) {
      primary.push(row)
    } else if (isSecondary) {
      secondary.push(row)
    }
    // else: query does not appear at all — drop the row.
  }

  return { primary, secondary }
}

/**
 * Returns the subset of `rows` whose searchable fields contain the
 * query as a token-prefix. Drops everything else (no substring
 * fallback). Empty / whitespace queries pass through unchanged.
 *
 * This is the "strict primary only" rule used across the app's flat
 * lists per AGENTS.md and the user's explicit ask: "I should not be
 * able to search for 'te' and see something with a 'te' in it unless
 * the 'te' is at the start."
 */
export function filterByTokenPrefix<T>(
  rows: readonly T[],
  query: string,
  getFields: (row: T) => ReadonlyArray<string | null | undefined>,
): T[] {
  const normalisedQuery = query.trim().toLowerCase()
  if (normalisedQuery.length === 0) return [...rows]

  const out: T[] = []
  for (const row of rows) {
    const fields = getFields(row)
    for (const field of fields) {
      if (field === null || field === undefined) continue
      if (hasTokenPrefixLowered(field.toLowerCase(), normalisedQuery)) {
        out.push(row)
        break
      }
    }
  }
  return out
}

// Internal helper — assumes both inputs are already lower-cased and
// the query is already trimmed. Implements the same match rules as
// `hasTokenPrefix` (whole-string prefix OR token-AND), without the
// per-call normalisation overhead.
function hasTokenPrefixLowered(loweredValue: string, loweredQuery: string): boolean {
  if (loweredValue.startsWith(loweredQuery)) return true
  const valueTokens = loweredValue.split(TOKEN_BOUNDARY).filter(nonEmpty)
  const queryTokens = loweredQuery.split(TOKEN_BOUNDARY).filter(nonEmpty)
  if (queryTokens.length === 0) return true
  for (const qToken of queryTokens) {
    if (!someStartsWith(valueTokens, qToken)) return false
  }
  return true
}
